/**
 * NMS Color Swatches Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Provides progressive enhancement for color-as-product switching
 * Uses Shopify Section Rendering API for seamless product updates
 */

class NMSColorSwatches extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #abortController = null;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.swatchId = element.dataset.swatchId || `swatches-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from Liquid
    this.sectionId = element.dataset.sectionId;
    this.productId = element.dataset.productId;
    this.mainSectionId = element.dataset.mainSectionId;
    this.sectionWrapperId = element.dataset.sectionWrapperId;

    // Store additional sections to update (comma-separated list)
    this.additionalSections = element.dataset.additionalSections || '';

    // Control whether URL should be updated on swatch change
    // Default: true for backward compatibility (PDP behavior)
    // Set to 'false' on landing pages to prevent URL changes
    this.updateUrl = element.dataset.updateUrl !== 'false';

    // REQUIRED: Store in global registry for external access
    window.neemans.colorSwatches = window.neemans.colorSwatches || {};
    window.neemans.colorSwatches[this.swatchId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    this.#setupColorSwatchesRefs();
    this.#initializeColorState();
    this.#bindColorEvents();
    this.#setupPopStateHandler();
    this.#listenForSectionUpdates();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupColorSwatchesRefs() {
    // Query only within component scope using data attributes
    this.refs.swatchList = this.element.querySelector('[ref="swatchList"]');
    this.refs.swatches = this.element.querySelectorAll('[ref="swatches[]"]');
  }

  #initializeColorState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`colorSwatches.${this.swatchId}`, {
      currentProduct: this.productId,
      isLoading: false,
      activeHandle: null,
    });
  }

  // Re-initialize after NMSProductRecommendations replaces the section HTML
  #listenForSectionUpdates() {
    const handler = event => {
      const container = event.detail?.container;
      if (!container) return;
      const newElement = container.querySelector('[data-nms-color-swatches]');
      if (newElement && newElement !== this.element) {
        this.element = newElement;
        this.#bindColorEvents();
      }
    };
    document.addEventListener('nms:recommendation-content-updated', handler);
    this.#eventListeners.push({ element: document, event: 'nms:recommendation-content-updated', handler });
  }

  #bindColorEvents() {
    // Bind click handlers within this component and replace any stale bindings
    // retained from an earlier section render
    for (const listener of this.#eventListeners.filter(({ event }) => event === 'click')) {
      listener.element.removeEventListener(listener.event, listener.handler, listener.capture);
    }
    this.#eventListeners = this.#eventListeners.filter(({ event }) => event !== 'click');

    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        const handler = event => {
          this[methodName].call(this, event, ...args);
        };

        element.addEventListener('click', handler, { capture: true });
        this.#eventListeners.push({ element, event: 'click', handler, capture: true });
      } else {
        console.error('Method not found:', methodName);
      }
    }
  }

  // Public API method - called via data-nms-click
  async handleSwatchClick(event, productHandle) {
    event.preventDefault();
    event.stopPropagation();

    const swatchLink = event.currentTarget;

    // Don't do anything if clicking the same product
    if (swatchLink.classList.contains('nms-color-swatches__swatch--active')) {
      return;
    }

    // Immediately block further clicks before any async work
    this.#showLoading();

    // Abort any in-flight request and start fresh
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    try {
      // Get URL and title from data attributes
      const productUrl = swatchLink.dataset.productUrl;
      const productTitle = swatchLink.dataset.productTitle;

      // Update global state
      this.setGlobalData(`colorSwatches.${this.swatchId}.isLoading`, true);

      await this.#switchToProduct(productUrl, productHandle, productTitle, this.#abortController.signal);

      // Dispatch success event
      this.dispatchNMSEvent('color-swatch:changed', {
        swatchId: this.swatchId,
        productHandle: productHandle,
        productUrl: productUrl,
        productTitle: productTitle,
        component: this,
      });
    } catch (error) {
      if (error.name === 'AbortError') return; // Superseded by a newer click — do nothing

      this.handleError(error, 'Failed to switch color. Redirecting...');
      console.error('Color switch failed:', error);

      // Fallback to hard reload
      setTimeout(() => {
        window.location.href = swatchLink.dataset.href || swatchLink.dataset.productUrl;
      }, 1000);
    } finally {
      this.setGlobalData(`colorSwatches.${this.swatchId}.isLoading`, false);
    }
  }

  async #switchToProduct(productUrl, productHandle, productTitle, signal) {
    try {
      // Build sections parameter - include main section and any additional sections
      const sectionsToFetch = [this.mainSectionId];

      if (this.additionalSections) {
        // Split comma-separated section types and find their IDs on the page
        const additionalTypes = this.additionalSections.split(',').map(s => s.trim());

        additionalTypes.forEach(sectionType => {
          // Find section by data-section-type attribute (more reliable than ID matching)
          const sectionElement = document.querySelector(`[data-section-type="${sectionType}"]`);
          if (sectionElement && sectionElement.dataset.sectionId) {
            // Use the explicit section ID from the data attribute
            sectionsToFetch.push(sectionElement.dataset.sectionId);
          }
        });
      }

      // Join all section IDs with comma for the API call
      const sectionsParam = sectionsToFetch.join(',');

      // Fetch the new product content using Section Rendering API
      const response = await fetch(`${productUrl}?sections=${sectionsParam}`, { signal });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data[this.mainSectionId]) {
        throw new Error(`Invalid response: missing ${this.mainSectionId} section`);
      }

      // NMS INP: let the loading state paint before the section swap, and put
      // each section swap in its own task. Replacing the whole main section is
      // a long block of parse + layout work; running it all in one task made
      // any tap that landed during it wait. The abort check keeps a superseded
      // click from rendering stale content, the same way fetch would.
      await this.#yieldToMain();

      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      // Update the main product section
      await this.#updateProductSection(data[this.mainSectionId]);

      // Update additional sections if they were fetched
      for (const sectionId of sectionsToFetch.slice(1)) {
        if (!data[sectionId]) continue;

        await this.#yieldToMain();

        if (signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        this.#updateAdditionalSection(sectionId, data[sectionId]);
      }

      // Update the browser URL (only on PDP, not on landing pages)
      if (this.updateUrl) {
        this.#updateUrl(productUrl, productTitle);
      }

      // Update active state
      this.#updateActiveState(productHandle);

      // Update global state
      this.setGlobalData(`colorSwatches.${this.swatchId}.currentProduct`, productHandle);
      this.setGlobalData(`colorSwatches.${this.swatchId}.activeHandle`, productHandle);
    } finally {
      // Only restore UI if this request wasn't superseded by a newer click
      if (!signal.aborted) {
        this.#hideLoading();
      }
    }
  }

  async #updateProductSection(newHtml) {
    // Target the entire section wrapper
    const sectionWrapper = document.querySelector(`#${this.sectionWrapperId}`);

    if (!sectionWrapper) {
      throw new Error('Main section wrapper not found');
    }

    // Create temporary container to parse new HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#${this.sectionWrapperId}`);

    if (!newSectionWrapper) {
      throw new Error('New section wrapper not found in response');
    }

    // Replace the entire section content
    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    // Re-initialize components in the new content
    this.#reinitializeComponents(sectionWrapper);
  }

  #updateAdditionalSection(sectionId, newHtml) {
    // Find the section wrapper by ID
    const sectionWrapper = document.querySelector(`#shopify-section-${sectionId}`);

    if (!sectionWrapper) {
      console.warn(`Additional section not found: shopify-section-${sectionId}`);
      return;
    }

    // Create temporary container to parse new HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#shopify-section-${sectionId}`);

    if (!newSectionWrapper) {
      console.warn(`New section wrapper not found in response for: ${sectionId}`);
      return;
    }

    // Replace the entire section content
    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    // Dispatch event for the updated section
    this.dispatchNMSEvent(
      'color-section-updated',
      {
        sectionId: sectionId,
        container: sectionWrapper,
        source: 'color-swatches',
        swatchId: this.swatchId,
        component: this,
      },
      sectionWrapper
    );
  }

  #reinitializeComponents(container) {
    // Find new color swatches element in updated content
    const newColorSwatches = container.querySelector('[data-nms-color-swatches]');

    if (newColorSwatches && newColorSwatches !== this.element) {
      // Update element reference and re-bind events
      this.element = newColorSwatches;
      this.#bindColorEvents();
    }

    // Fire NMS custom event for other components to re-initialize
    this.dispatchNMSEvent(
      'color-content-updated',
      {
        container: container,
        source: 'color-swatches',
        swatchId: this.swatchId,
        component: this,
      },
      container
    );
  }

  #updateUrl(newUrl, productTitle) {
    const newState = {
      nmsProductSwitch: true,
      product: productTitle,
      url: newUrl,
      timestamp: Date.now(),
      swatchId: this.swatchId,
    };

    history.pushState(newState, productTitle, newUrl);

    // Update page title
    document.title = document.title.replace(/^[^|]*/, productTitle);

    // Dispatch URL update event
    this.dispatchNMSEvent('color-swatch:url-updated', {
      swatchId: this.swatchId,
      newUrl: newUrl,
      productTitle: productTitle,
      component: this,
    });
  }

  #updateActiveState(activeHandle) {
    // Remove active class from all swatches
    const allSwatches = this.element.querySelectorAll('.nms-color-swatches__swatch');

    for (const swatch of allSwatches) {
      swatch.classList.remove('nms-color-swatches__swatch--active');
    }

    // Add active class to current swatch
    const activeSwatch = this.element.querySelector(`[data-product-handle="${activeHandle}"]`);

    if (activeSwatch) {
      activeSwatch.classList.add('nms-color-swatches__swatch--active');
    }

    // Dispatch active state change event
    this.dispatchNMSEvent('color-swatch:active-changed', {
      swatchId: this.swatchId,
      activeHandle: activeHandle,
      component: this,
    });
  }

  /* NMS INP: hand the main thread back so the browser can paint and service
     any queued input before we continue with heavy DOM work. */
  #yieldToMain() {
    return new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
  }

  #showLoading() {
    if (this.element) {
      this.element.style.opacity = '0.6';
      this.element.style.pointerEvents = 'none';
      this.element.setAttribute('aria-busy', 'true');
      this.element.classList.add('nms-loading');
    }
  }

  #hideLoading() {
    if (this.element) {
      this.element.style.opacity = '';
      this.element.style.pointerEvents = '';
      this.element.setAttribute('aria-busy', 'false');
      this.element.classList.remove('nms-loading');
    }
  }

  #setupPopStateHandler() {
    // Tag the initial page state so the popstate handler can identify it
    if (!history.state?.nmsProductSwitch) {
      history.replaceState(
        { nmsProductSwitch: true, url: window.location.href },
        document.title,
        window.location.href
      );
    }

    // Register a single global handler — both color swatches and product recommendations share it
    if (!window.__nmsPopstateHandlerAdded) {
      window.__nmsPopstateHandlerAdded = true;
      window.addEventListener('popstate', event => {
        if (event.state?.nmsProductSwitch) {
          window.location.reload();
        }
      });
    }
  }

  // Public API method for external access
  getCurrentProductHandle() {
    return this.getGlobalData(`colorSwatches.${this.swatchId}.activeHandle`);
  }

  // Public API method for external access
  isLoading() {
    return this.getGlobalData(`colorSwatches.${this.swatchId}.isLoading`) || false;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler, listener.capture);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.colorSwatches?.[this.swatchId]) {
      delete window.neemans.colorSwatches[this.swatchId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSColorSwatches() {
  if (!window.NMSBaseClass) {
    console.error('NMSColorSwatches: NMSBaseClass required');
    return;
  }

  // Find all color swatch components
  const elements = document.querySelectorAll('[data-nms-color-swatches]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSColorSwatches(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSColorSwatches);
} else {
  initNMSColorSwatches();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-color-swatches]')) {
    initNMSColorSwatches();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSColorSwatches = NMSColorSwatches;

// Global initialization function for other components to use
window.initNMSColorSwatches = initNMSColorSwatches;
